import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Card,
  Row,
  Col,
  Typography,
  Space,
  Progress,
  Table,
  Badge,
  Tag,
  Spin,
  Alert,
} from "antd";
import {
  UserOutlined,
  IdcardOutlined,
  TeamOutlined,
  ClockCircleOutlined,
} from "@ant-design/icons";
import {
  AccumulationChartComponent,
  AccumulationSeriesCollectionDirective,
  AccumulationSeriesDirective,
  AccumulationLegend,
  PieSeries,
  AccumulationDataLabel,
  AccumulationTooltip,
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  Legend,
  Category,
  ColumnSeries,
  Tooltip,
} from "@syncfusion/ej2-react-charts";
import AppContext from "../../AppContext";
import { BASE_URL } from "../../Constants";
import axios from "axios";

const { Title, Text } = Typography;

// Fallback data for when API data is not available
const fallbackEmployeeStats = {
  active: 0,
  inactive: 0,
  total: 0,
  withCards: 0,
  recentlyAdded: 0,
};

const CompanyDashboard = () => {
  const { t } = useTranslation();
  const { profile } = useContext(AppContext);
  const [dashboardData, setDashboardData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  // Organization-wide employee usage (Parent + all direct Sub-Companies).
  const [orgUsage, setOrgUsage] = React.useState(null);

  // Calculate stats from API data
  const getStats = () => {
    if (!dashboardData) return fallbackEmployeeStats; // fallback to empty data

    const { employeeStatistics } = dashboardData;
    return {
      active: employeeStatistics.activeEmployees,
      inactive: employeeStatistics.inactiveEmployees,
      total: employeeStatistics.totalEmployees,
      withCards: employeeStatistics.employeesWithDigitalCards,
      recentlyAdded: employeeStatistics.recentlyAddedEmployees,
    };
  };

  const stats = getStats();
  const inactivePercentage =
    stats.total > 0 ? Math.round((stats.inactive / stats.total) * 100) : 0;
  const activePercentage =
    stats.total > 0 ? Math.round((stats.active / stats.total) * 100) : 0;

  // Organization-wide employee usage: Parent + all direct Sub-Companies vs the Parent's pooled limit.
  // Falls back to this company's own numbers if the usage endpoint is unavailable.
  const orgEmployeeCount =
    orgUsage?.used != null ? orgUsage.used : stats.total;
  const orgEmployeeMax =
    orgUsage?.max != null
      ? orgUsage.max
      : dashboardData?.subscriptionInfo?.maxEmployees || 0;
  const orgUsagePercent =
    orgEmployeeMax > 0
      ? Math.round((orgEmployeeCount / orgEmployeeMax) * 100)
      : 0;

  const StatCard = ({ title, value, subtitle, icon: Icon, color }) => (
    <Card size="small" bodyStyle={{ padding: "15px" }}>
      <Space direction="horizontal" align="start" size="middle">
        <div
          style={{
            backgroundColor: `${color}20`,
            padding: "8px",
            borderRadius: "8px",
          }}
        >
          <Icon style={{ fontSize: 20, color: color }} />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: "12px" }}>
            {title}
          </Text>
          <div style={{ fontSize: "18px", fontWeight: "bold" }}>
            {value?.toLocaleString()}
          </div>
          {subtitle && (
            <Text type="secondary" style={{ fontSize: "11px" }}>
              {subtitle}
            </Text>
          )}
        </div>
      </Space>
    </Card>
  );

  const activityColumns = [
    {
      title: "Employee",
      dataIndex: "employee",
      key: "employee",
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Time",
      dataIndex: "time",
      key: "time",
      render: (text) => <Text type="secondary">{text}</Text>,
    },
  ];

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(
        `${BASE_URL}/Dashboard/admin/${profile.companyId}`
      );
      setDashboardData(response.data);
      try {
        const usage = await axios.get(
          `${BASE_URL}/Employee/organization-usage?companyId=${profile.companyId}`
        );
        setOrgUsage(usage.data);
      } catch (e) {
        console.error("Error fetching organization usage:", e);
      }
      setLoading(false);
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError(err.message || "Failed to load dashboard data");
      setLoading(false);
    }
  };
  useEffect(() => {
    if (profile?.companyId) {
      fetchDashboardData();
    }
  }, [profile?.companyId]);

  if (loading) {
    return (
      <div
        style={{
          padding: "24px",
          background: "#f5f5f5",
          borderRadius: "15px",
          minHeight: "100vh",
        }}
        className="flex justify-center items-center gap-4"
      >
        <Spin size="large" />
        <div>
          <Text type="secondary">{t("dashboard.loadingDashboardData")}</Text>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "16px" }}>
        <Alert
          message={t("dashboard.errorLoadingDashboard")}
          description={error}
          type="error"
          showIcon
          action={
            <button
              onClick={fetchDashboardData}
              style={{
                border: "none",
                background: "none",
                color: "#1890ff",
                cursor: "pointer",
              }}
            >
              {t("dashboard.retry")}
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "24px",
        background: "#f5f5f5",
        borderRadius: "15px",
        minHeight: "100vh",
      }}
    >
      <Row gutter={[16, 16]}>
        {/* Stats Cards */}
        <Col xs={24} sm={12} md={6}>
          <StatCard
            title={t("dashboard.totalEmployees")}
            value={stats.total}
            subtitle={t("dashboard.allEmployees")}
            icon={TeamOutlined}
            color="#1890ff"
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <StatCard
            title={t("dashboard.activeEmployees")}
            value={stats.active}
            subtitle={`${activePercentage}% ${t("dashboard.ofTotal")}`}
            icon={UserOutlined}
            color="#52c41a"
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <StatCard
            title={t("dashboard.inactiveEmployees")}
            value={stats.inactive}
            subtitle={`${inactivePercentage}% ${t("dashboard.ofTotal")}`}
            icon={ClockCircleOutlined}
            color="#faad14"
          />
        </Col>

        <Col xs={24} sm={12} md={6}>
          <StatCard
            title={t("dashboard.digitalCardsIssued")}
            value={stats.withCards}
            subtitle={`${
              stats.total > 0
                ? Math.round((stats.withCards / stats.total) * 100)
                : 0
            }% ${t("dashboard.ofEmployees")}`}
            icon={IdcardOutlined}
            color="#13c2c2"
          />
        </Col>

        {/* Additional row for recently added */}
        <Col xs={24} sm={12} md={6}>
          <StatCard
            title={t("dashboard.recentlyAdded")}
            value={stats.recentlyAdded}
            subtitle={t("dashboard.newEmployees")}
            icon={UserOutlined}
            color="#722ed1"
          />
        </Col>
        {/* Main Content */}
        <Col xs={24}>
          <Row gutter={[16, 16]}>
            {/* Employee Status Distribution */}
            <Col xs={24} lg={12}>
              <Card
                size="small"
                title={t("dashboard.employeeStatusDistribution")}
                style={{ height: "400px" }}
                bodyStyle={{ height: "340px", padding: "8px" }}
              >
                <AccumulationChartComponent
                  id="employee-status-pie"
                  tooltip={{ enable: true }}
                  legendSettings={{
                    visible: true,
                    position: "Bottom",
                    textStyle: { size: "12px" },
                  }}
                  height="320px"
                  width="100%"
                >
                  <Inject
                    services={[
                      AccumulationLegend,
                      PieSeries,
                      AccumulationDataLabel,
                      AccumulationTooltip,
                    ]}
                  />
                  <AccumulationSeriesCollectionDirective>
                    <AccumulationSeriesDirective
                      dataSource={[
                        { status: t("dashboard.activeEmployees"), count: stats.active },
                        { status: t("dashboard.inactiveEmployees"), count: stats.inactive },
                      ]}
                      xName="status"
                      yName="count"
                      innerRadius="30%"
                      radius="70%"
                      emptyPointSettings={{ mode: "Average" }}
                      dataLabel={{
                        visible: true,
                        position: "Inside",
                        name: "text",
                        font: { size: "11px" },
                      }}
                      palettes={["#52c41a", "#faad14"]}
                    />
                  </AccumulationSeriesCollectionDirective>
                </AccumulationChartComponent>
              </Card>
            </Col>

            {/* Subscription Information */}
            <Col xs={24} lg={12}>
              <Card
                size="small"
                title={t("dashboard.subscriptionInformation")}
                style={{ height: "400px" }}
                bodyStyle={{ padding: "16px" }}
              >
                {dashboardData?.subscriptionInfo ? (
                  <Space
                    direction="vertical"
                    size="medium"
                    style={{ width: "100%" }}
                  >
                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.plan")}</Text>
                          <Title
                            level={4}
                            style={{ margin: 0, color: "#1890ff" }}
                          >
                            {dashboardData.subscriptionInfo.planName}
                          </Title>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.status")}</Text>
                          <Badge
                            status={
                              dashboardData.subscriptionInfo.status === "Active"
                                ? "success"
                                : "warning"
                            }
                            text={dashboardData.subscriptionInfo.status}
                            style={{ fontSize: "14px" }}
                          />
                        </Space>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.employeeLimit")}</Text>
                          <Text strong>
                            {orgEmployeeMax > 0 ? orgEmployeeMax : "—"}
                          </Text>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.price")}</Text>
                          <Text strong>
                            SAR {dashboardData.subscriptionInfo.price}
                          </Text>
                        </Space>
                      </Col>
                    </Row>

                    <div>
                      <Text type="secondary">{t("dashboard.employeeUsage")}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Progress
                          percent={orgUsagePercent}
                          strokeColor={
                            orgEmployeeMax > 0 && orgEmployeeCount >= orgEmployeeMax
                              ? "#ff4d4f"
                              : orgEmployeeMax > 0 &&
                                orgEmployeeCount >= orgEmployeeMax * 0.8
                              ? "#faad14"
                              : "#52c41a"
                          }
                          format={() =>
                            orgEmployeeMax > 0
                              ? `${orgEmployeeCount}/${orgEmployeeMax}`
                              : `${orgEmployeeCount}`
                          }
                        />
                      </div>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.duration")}</Text>
                          <Text strong>
                            {dashboardData.subscriptionInfo.durationType}
                          </Text>
                        </Space>
                      </Col>
                      <Col span={12}>
                        <Space direction="vertical" size="small">
                          <Text type="secondary">{t("dashboard.autoRenewal")}</Text>
                          <Badge
                            status={
                              dashboardData.subscriptionInfo.isAutoRenewal
                                ? "success"
                                : "default"
                            }
                            text={
                              dashboardData.subscriptionInfo.isAutoRenewal
                                ? t("dashboard.enabled")
                                : t("dashboard.disabled")
                            }
                          />
                        </Space>
                      </Col>
                    </Row>

                    <div>
                      <Text type="secondary">{t("dashboard.daysUntilExpiry")}</Text>
                      <div style={{ marginTop: 8 }}>
                        <Progress
                          percent={Math.max(
                            0,
                            Math.min(
                              100,
                              ((365 -
                                dashboardData.subscriptionInfo
                                  .daysUntilExpiry) /
                                365) *
                                100
                            )
                          )}
                          strokeColor={
                            dashboardData.subscriptionInfo.daysUntilExpiry <= 30
                              ? "#ff4d4f"
                              : dashboardData.subscriptionInfo
                                  .daysUntilExpiry <= 90
                              ? "#faad14"
                              : "#52c41a"
                          }
                          format={() =>
                            `${dashboardData.subscriptionInfo.daysUntilExpiry} days`
                          }
                        />
                      </div>
                    </div>

                    <Row gutter={[16, 8]}>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {t("dashboard.startDate")}
                        </Text>
                        <div>
                          {new Date(
                            dashboardData.subscriptionInfo.startDate
                          ).toLocaleDateString()}
                        </div>
                      </Col>
                      <Col span={12}>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                          {t("dashboard.endDate")}
                        </Text>
                        <div>
                          {new Date(
                            dashboardData.subscriptionInfo.endDate
                          ).toLocaleDateString()}
                        </div>
                      </Col>
                    </Row>
                  </Space>
                ) : (
                  <div style={{ textAlign: "center", paddingTop: "80px" }}>
                    <Text type="secondary">
                      {t("dashboard.noSubscriptionInfo")}
                    </Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Company Overview */}
            <Col xs={24}>
              <Card size="small" title={t("dashboard.companyOverview")}>
                <Row gutter={16}>
                  <Col span={12}>
                    <Title level={5}>{t("dashboard.companyInformation")}</Title>
                    {dashboardData?.companyInfo ? (
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: "100%" }}
                      >
                        <div>
                          <Text strong>{dashboardData.companyInfo.name}</Text>
                          {dashboardData.companyInfo.arabicName && (
                            <div style={{ fontSize: "12px", color: "#666" }}>
                              {dashboardData.companyInfo.arabicName}
                            </div>
                          )}
                        </div>
                        <div>
                          <Text type="secondary">{t("dashboard.email")}: </Text>
                          <Text>{dashboardData.companyInfo.email}</Text>
                        </div>
                        <div>
                          <Text type="secondary">{t("dashboard.status")}: </Text>
                          <Badge
                            status={
                              dashboardData.companyInfo.isActive
                                ? "success"
                                : "error"
                            }
                            text={
                              dashboardData.companyInfo.isActive
                                ? t("dashboard.active")
                                : t("dashboard.inactive")
                            }
                          />
                        </div>
                        <div>
                          <Text type="secondary">{t("dashboard.subscribed")}: </Text>
                          <Badge
                            status={
                              dashboardData.companyInfo.isSubscribed
                                ? "success"
                                : "error"
                            }
                            text={
                              dashboardData.companyInfo.isSubscribed
                                ? t("common.yes")
                                : t("common.no")
                            }
                          />
                        </div>
                        <div>
                          <Text type="secondary">{t("dashboard.created")}: </Text>
                          <Text>
                            {new Date(
                              dashboardData.companyInfo.createdDate
                            ).toLocaleDateString()}
                          </Text>
                        </div>
                      </Space>
                    ) : (
                      <Text type="secondary">
                        {t("dashboard.loadingCompanyInfo")}
                      </Text>
                    )}
                  </Col>
                  <Col span={12}>
                    <Title level={5}>{t("dashboard.quickStats")}</Title>
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: "100%" }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Card size="small" bodyStyle={{ padding: "8px" }}>
                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  color: "#1890ff",
                                }}
                              >
                                {activePercentage}%
                              </div>
                              <Text
                                type="secondary"
                                style={{ fontSize: "11px" }}
                              >
                                {t("dashboard.activeRate")}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                      <Row gutter={16}>
                        <Col span={12}>
                          <Card size="small" bodyStyle={{ padding: "8px" }}>
                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  color: "#52c41a",
                                }}
                              >
                                {orgUsagePercent}%
                              </div>
                              <Text
                                type="secondary"
                                style={{ fontSize: "11px" }}
                              >
                                {t("dashboard.planUsage")}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                        <Col span={12}>
                          <Card size="small" bodyStyle={{ padding: "8px" }}>
                            <div style={{ textAlign: "center" }}>
                              <div
                                style={{
                                  fontSize: "18px",
                                  fontWeight: "bold",
                                  color: "#722ed1",
                                }}
                              >
                                {stats.recentlyAdded}
                              </div>
                              <Text
                                type="secondary"
                                style={{ fontSize: "11px" }}
                              >
                                {t("dashboard.newThisPeriod")}
                              </Text>
                            </div>
                          </Card>
                        </Col>
                      </Row>
                    </Space>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </div>
  );
};

export default CompanyDashboard;
